import _ from "lodash"

import Hub from "../hub"
import log from "../services/log"
import Command from "./commands/command"
import Model, { Store } from "./model"
import { ID } from "./types"

export default class Commander {
  model: Model
  hub: Hub

  // TODO: should contain command GROUPS so that the place idea operation can be undone in one go
  // despite it being a two command operation
  history: Command[]
  nextCommandToUndoPointer: number | undefined

  syncCallback: (command: Command, storeBefore: Store) => void

  constructor() {
    this.history = []
    this.nextCommandToUndoPointer = undefined
  }

  exec(command: Command): void {
    if (command.undoable)
      if (this.nextCommandToUndoPointer !== undefined) {
        this.history = this.history.slice(0, this.nextCommandToUndoPointer + 1)
        this.nextCommandToUndoPointer = undefined
      }
    log("COMMAND", "#BDF", `command`, command.name, command.state)

    const storeBefore = this.model.store

    this.model.nextOriginator = "editor"
    command.local(this.model)

    if (command.undoable) {
      const merged = command.merge(this.history[this.history.length - 1])
      if (merged === undefined) this.history.push(command)
      else this.history[this.history.length - 1] = merged
    }

    if (command.syncable) {
      this.syncCallback(command, storeBefore)
    }

    this.hub.emit(`command:${command.name}`, command.state)
  }

  // Returns false if conflict resolution cancelled the command.
  execWithoutHistoryOrSync(command: Command): boolean {
    const newStateOrCancel = command.resolveConflicts(this.model)
    if (newStateOrCancel == null) {
      console.log("skipping command due to conflict resolution")
      return false
    }
    if (!_.isEqual(newStateOrCancel, command.state)) {
      command.unResolvedState = command.state
      command.state = newStateOrCancel
    }

    log("COMMAND (SYNC)", "#BDF", `command`, command.name, command.state)
    this.model.nextOriginator = "model"
    command.local(this.model)
    this.hub.emit(`command:${command.name}`, command.state)
    return true
  }

  undo(): any {
    this.nextCommandToUndoPointer ??= this.history.length - 1
    const command = this.history[this.nextCommandToUndoPointer]
    if (command == null) return
    log("MODEL", "#BDF", `undo`, command)
    const inverseCommands = command.undo()

    inverseCommands.forEach((c) => {
      const store = this.model.store
      this.model.nextOriginator = "model"
      c.local(this.model)
      if (c.syncable) this.syncCallback(c, store)
    })
    this.setOpenLocationAsCommandSideEffect(command)
    this.nextCommandToUndoPointer--

    inverseCommands.forEach((c) => {
      this.hub.emit(`command:${c.name}`, c.state)
    })
  }

  canUndo(): boolean {
    this.nextCommandToUndoPointer ??= this.history.length - 1
    const command = this.history[this.nextCommandToUndoPointer]
    return !!command
  }

  redo(): any {
    if (this.nextCommandToUndoPointer == undefined) return
    const commandToRedo = this.history[this.nextCommandToUndoPointer + 1]
    if (commandToRedo == null) return
    log("MODEL", "#BDF", `redo`, commandToRedo)
    const redoCommand = commandToRedo.redo()
    const store = this.model.store
    this.model.nextOriginator = "model"
    redoCommand.local(this.model)
    if (redoCommand.syncable) this.syncCallback(redoCommand, store)
    this.setOpenLocationAsCommandSideEffect(redoCommand)
    this.nextCommandToUndoPointer++
    if (this.nextCommandToUndoPointer >= this.history.length - 1)
      this.nextCommandToUndoPointer = undefined

    this.hub.emit(`command:${redoCommand.name}`, redoCommand.state)
  }

  canRedo(): boolean {
    if (this.nextCommandToUndoPointer == undefined) return false
    const commandToRedo = this.history[this.nextCommandToUndoPointer + 1]
    if (commandToRedo == null) return false
    return true
  }

  setOpenLocationAsCommandSideEffect(command: Command): any {
    const openIdeaTuple = command.openIdea()
    if (openIdeaTuple) {
      const [openIdeaID, selectionPos] = openIdeaTuple as [ID, number]
      const openIdea = this.model.getIdea(openIdeaID)
      if (openIdea) {
        this.hub.emit("navigation-history:set-open-location", {
          location: openIdea.location,
          selectionPos,
        })
      }
    }
  }
}
